import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AIContentApi } from 'app/modules/posts/api/aiContent';
import { ApiClientError } from 'api/client';
import { queryKey } from 'constants/queryKey';
import { useToast } from 'app/hooks/useToast';
import { GenerationsApi } from 'app/modules/posts/api/GenerationsApi';
import { useAiUsageLimitModal } from 'app/modules/aiContent/hooks/useAiUsageLimitModal';

type Props = {
  accountPlanType: string;
  disabled?: boolean;
};

export function useAiContentQueries({ accountPlanType, disabled }: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { openLimitModal } = useAiUsageLimitModal();

  const options = useQuery(
    queryKey.aiContentOptions({ planType: accountPlanType }),
    () => AIContentApi.options({ plan_type: accountPlanType }),
    {
      enabled: !disabled && !!accountPlanType,
      staleTime: 30_000,
    },
  );

  const limitReached = options.data?.remainingAttempts.all === 0;

  const transformText = useMutation(
    (
      values: Omit<
        Parameters<typeof GenerationsApi.transformText>[0],
        'planType'
      >,
    ) =>
      GenerationsApi.transformText({
        ...values,
        planType: accountPlanType,
      }),
    {
      onError(e: ApiClientError) {
        toast(e.userMessage ?? 'Something went wrong', 'error');
      },
      onSuccess() {
        queryClient.invalidateQueries(queryKey.aiContentOptions());
      },
    },
  );

  const guardedTransformText: typeof transformText = {
    ...transformText,
    mutate: (...args) => {
      if (limitReached) {
        openLimitModal();
        return;
      }
      transformText.mutate(...args);
    },
  };

  function guardClick<T extends unknown[]>(
    action?: (...args: T) => void,
  ): (...args: T) => void {
    return (...args: T) => {
      if (limitReached) {
        openLimitModal();
        return;
      }
      action?.(...args);
    };
  }

  return {
    transformText: guardedTransformText,
    options,
    limitReached,
    guardClick,
    openLimitModal,
  };
}

export default useAiContentQueries;
